import React from 'react';
import { motion } from 'framer-motion';
import './LegalPage.css';

const AccessibilityPage = () => {
  return (
    <div className="legal-page">
      <section className="legal-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1>הצהרת נגישות</h1>
            <p className="legal-subtitle">
              AIweon מחויבת להנגשת הפלטפורמה לכלל האוכלוסייה
            </p>
            <p className="legal-updated">עדכון אחרון: פברואר 2025</p>
          </motion.div>
        </div>
      </section>

      <div className="legal-content">
        {/* Commitment */}
        <section className="legal-section">
          <h2>מחויבות לנגישות</h2>
          <p>
            AIweon מאמינה כי האינטרנט צריך להיות נגיש לכל אדם, ללא קשר למוגבלות.
            אנו פועלים באופן מתמיד להנגשת הפלטפורמה שלנו ולשיפור חווית השימוש
            עבור כלל המשתמשים, לרבות אנשים עם מוגבלויות.
          </p>
          <div className="legal-info-box">
            <p>
              הפלטפורמה עומדת בדרישות תקנות שוויון זכויות לאנשים עם מוגבלות
              (התאמות נגישות לשירות), התשע"ג-2013, ובהתאם לתקן הישראלי ת"י 5568
              המבוסס על הנחיות WCAG 2.1 ברמה AA.
            </p>
          </div>
        </section>

        {/* Standards */}
        <section className="legal-section">
          <h2>תקנים ורגולציה</h2>
          <p>הנגשת הפלטפורמה מתבצעת בהתאם לתקנים הבאים:</p>
          <ul>
            <li>חוק שוויון זכויות לאנשים עם מוגבלות, התשנ"ח-1998 (פרק ה'1)</li>
            <li>תקנות שוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות), התשע"ג-2013</li>
            <li>תקן ישראלי ת"י 5568 — הנגשת תכנים באינטרנט</li>
            <li>הנחיות WCAG 2.1 ברמת תאימות AA (Web Content Accessibility Guidelines)</li>
          </ul>
        </section>

        {/* What We've Done */}
        <section className="legal-section">
          <h2>פעולות הנגשה שבוצעו</h2>
          <p>במסגרת מחויבותנו לנגישות, בוצעו הפעולות הבאות באתר:</p>
          <ul>
            <li>התאמה מלאה לניווט באמצעות מקלדת — כל רכיבי הממשק נגישים ללא שימוש בעכבר</li>
            <li>תמיכה בקוראי מסך (Screen Readers) — שימוש בתגיות ARIA ותוויות נגישות מתאימות</li>
            <li>יחסי ניגודיות צבעים — עמידה בדרישות ניגודיות מינימלית בין טקסט לרקע (4.5:1)</li>
            <li>מבנה סמנטי — שימוש בתגיות HTML סמנטיות (header, nav, main, footer) לניווט ברור</li>
            <li>טקסט חלופי — תמונות כוללות תיאורים מילוליים (alt text) עבור קוראי מסך</li>
            <li>גופנים וגודל טקסט — אפשרות הגדלת טקסט עד 200% ללא אובדן תוכן או פונקציונליות</li>
            <li>טפסים נגישים — כל שדה טופס כולל תווית ברורה, הודעות שגיאה מובנות ותיאור מטרה</li>
            <li>תמיכה ב-RTL — הממשק מותאם במלואו לכיוון כתיבה מימין לשמאל (עברית)</li>
            <li>רספונסיביות — התאמה מלאה למסכים בכל גודל, כולל מכשירים ניידים וטאבלטים</li>
            <li>אנימציות מותאמות — כיבוד הגדרת prefers-reduced-motion של המשתמש</li>
          </ul>
        </section>

        {/* Supported Technologies */}
        <section className="legal-section">
          <h2>טכנולוגיות ודפדפנים נתמכים</h2>
          <p>הפלטפורמה נבדקת ונתמכת בדפדפנים ובטכנולוגיות הבאות:</p>
          <table className="legal-table">
            <thead>
              <tr>
                <th>דפדפן / טכנולוגיה</th>
                <th>גרסה מינימלית</th>
                <th>סטטוס</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Google Chrome</td>
                <td>90+</td>
                <td>נתמך</td>
              </tr>
              <tr>
                <td>Mozilla Firefox</td>
                <td>88+</td>
                <td>נתמך</td>
              </tr>
              <tr>
                <td>Apple Safari</td>
                <td>14+</td>
                <td>נתמך</td>
              </tr>
              <tr>
                <td>Microsoft Edge</td>
                <td>90+</td>
                <td>נתמך</td>
              </tr>
              <tr>
                <td>NVDA (קורא מסך)</td>
                <td>2021+</td>
                <td>נתמך</td>
              </tr>
              <tr>
                <td>VoiceOver (Apple)</td>
                <td>—</td>
                <td>נתמך</td>
              </tr>
              <tr>
                <td>JAWS (קורא מסך)</td>
                <td>2020+</td>
                <td>נתמך</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* Known Issues */}
        <section className="legal-section">
          <h2>סייגים ובעיות ידועות</h2>
          <p>
            למרות מאמצינו להנגיש את כלל רכיבי הפלטפורמה, ייתכן שחלק מהתכנים
            טרם הונגשו במלואם. אנו ממשיכים לפעול לשיפור מתמיד. להלן סייגים ידועים:
          </p>
          <ul>
            <li>תוכן שנוצר על ידי משתמשים (תמונות, סרטונים שהועלו) — עשוי שלא לכלול טקסט חלופי מלא</li>
            <li>קישורים חיצוניים לרשתות חברתיות — נגישות הפלטפורמות החיצוניות אינה באחריותנו</li>
            <li>מסמכי PDF שהועלו — ייתכן שלא יהיו נגישים במלואם</li>
          </ul>
          <p>
            אנו ממשיכים לעבוד על שיפור הנגישות ופותרים בעיות ברגע שאנו מזהים אותן.
          </p>
        </section>

        {/* Contact */}
        <section className="legal-section">
          <h2>פניות בנושא נגישות</h2>
          <p>
            אם נתקלתם בבעיית נגישות באתר, או שיש לכם הצעות לשיפור, נשמח לשמוע מכם.
            אנו מתחייבים לטפל בכל פנייה בנושא נגישות תוך 14 ימי עסקים.
          </p>
          <div className="legal-contact-card">
            <h3>AIweon — רכז/ת נגישות</h3>
            <p>דוא"ל: <a href="mailto:accessibility@aiweon.com">accessibility@aiweon.com</a></p>
            <p>טלפון: <a href="tel:+972522398911">052-239-8911</a></p>
            <p>
              בנוסף, ניתן לפנות לנציבות שוויון זכויות לאנשים עם מוגבלות במשרד המשפטים:
            </p>
            <p>טלפון: <a href="tel:*6763">*6763</a></p>
            <p>אתר: <a href="https://www.gov.il/he/departments/units/equal_rights_for_persons_with_disabilities_commission" target="_blank" rel="noopener noreferrer">נציבות שוויון זכויות לאנשים עם מוגבלות</a></p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AccessibilityPage;
